import type { Schemas } from '@kanos/api-client';
import { clientKindLabel } from '@kanos/domain';
import type { PaletteResults } from '@kanos/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFeature } from './firm/feature-gate';
import { clientsQuery } from './firm/intake-queries';
import { useMe } from './me';

type ClientEntry = Schemas['ClientEntry'];

/** How long typing pauses before the clients are asked; fewer letters find too much. */
const SETTLE_MS = 250;
const MIN_LETTERS = 2;

/**
 * The firm's clients for the command palette (after Corly's global search): by name or address,
 * through the client list - the same rights, the same firm, nothing of another. An intake opens
 * its page, a prospect the inquiries with their address.
 */
export function useClientSearch(): {
  results: PaletteResults | undefined;
  onQueryChange: (query: string) => void;
} {
  const me = useMe();
  const navigate = useNavigate();
  const has = useFeature();
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed.trim()), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [typed]);
  const wanted = has('check-point') && query.length >= MIN_LETTERS;
  const clients = useQuery({
    ...clientsQuery(me.activeTenantId, { q: query, page: 1, limit: 10 }),
    enabled: wanted,
  });
  if (!has('check-point') || typed.trim().length < MIN_LETTERS) {
    return { results: undefined, onQueryChange: setTyped };
  }
  return {
    onQueryChange: setTyped,
    results: {
      group: 'Mandanten',
      pending: !wanted || clients.isFetching,
      commands: (wanted ? (clients.data?.items ?? []) : []).map((client: ClientEntry) => ({
        id: `client:${client.caseId ?? client.inquiryId ?? client.email}`,
        label: client.name,
        group: 'Mandanten',
        keywords: `${client.email} ${clientKindLabel[client.kind]}`,
        icon: client.kind === 'private' ? User : Building2,
        onAction: () =>
          void (client.caseId
            ? navigate({ to: '/clients/$caseId', params: { caseId: client.caseId } })
            : navigate({ to: '/inquiries', search: { q: client.email } })),
      })),
    },
  };
}
